import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { z } from 'zod';

import { queries } from '@/queries';
import type { BlogType } from '@/services/strapi/blogs';

import Button from '../Button';
import Text from '../Text';
import PostCard from './PostCard';

const PAGE_SIZE = 3;
const ReadOtherArticles = ({
  blogType,
  tagDocumentId,
}: {
  blogType: BlogType;
  tagDocumentId?: string;
}) => {
  const [page, setPage] = useState(0);

  const { query } = useRouter();
  const slug = z.string().optional().parse(query.slug);

  const { data } = useQuery(
    queries.blog.baseDataList({
      pageSize: PAGE_SIZE,
      page,
      slugToOmit: slug,
      tagDocumentId,
      blogType,
    })
  );

  const maxPages = Math.ceil((data?.meta?.pagination.total ?? 0) / PAGE_SIZE);

  const displayedPage = page + 1;
  const isFirstPage = displayedPage === 1;
  const isLastPage = maxPages === displayedPage;

  if (data && !data.data.length) return null;

  return (
    <div className="flex w-full flex-col gap-6 pb-10">
      <div className="flex flex-col justify-between gap-4 lg:flex-row">
        <Text variant="h4" className="font-semibold">
          Read other articles
        </Text>
        <div className="flex flex-row items-center gap-4">
          {!isFirstPage && (
            <Button
              prefixIcon="material-symbols-light:arrow-back"
              iconClassName="text-h3"
              variant="unstyled"
              size="xs"
              onClick={() => setPage((prev) => prev - 1)}
            />
          )}
          <Text variant="h5">
            {displayedPage} / {maxPages}
          </Text>
          {!isLastPage && (
            <Button
              prefixIcon="material-symbols-light:arrow-back"
              variant="unstyled"
              size="xs"
              iconClassName="text-h3 rotate-180"
              onClick={() => setPage((prev) => prev + 1)}
            />
          )}
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-3">
        {data?.data.map(
          ({ createdAt, standardImage, slug: itemSlug, tags, title }) => (
            <PostCard
              key={itemSlug}
              createdAt={createdAt}
              img={standardImage}
              slug={itemSlug}
              tag={tags[0]?.tag}
              title={title}
              blogType={blogType}
            />
          )
        )}
      </div>
    </div>
  );
};

export default ReadOtherArticles;
